import * as React from "react";

import { SectionHead, Tag } from "@/components/organization/kit";
import SurfaceCard from "@/components/primitives/SurfaceCard";
import { milestoneLabel, type OnboardingStage } from "@/lib/onboardingPlan";

/** The in-page anchor the Overview summary links or scrolls to. */
export const ONBOARDING_PLAN_ANCHOR_ID = "onboarding-plan";

interface StageListProps {
  heading: string;
  items: string[];
}

/** One labelled list inside a stage; renders nothing when the list is empty. */
const StageList = ({ heading, items }: StageListProps) => {
  if (items.length === 0) return null;
  return (
    <div>
      <h4 className="eyebrow mb-2 text-ink-500">{heading}</h4>
      <ul className="m-0 flex list-disc flex-col gap-1.5 pl-5 text-[14px] leading-[1.5] text-ink-700">
        {items.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    </div>
  );
};

interface OnboardingPlanProps {
  /** Already normalized — see normalizeOnboardingStages. Untitled stages are never passed here. */
  stages: OnboardingStage[];
  id?: string;
}

/**
 * The complete New Agent Onboarding Plan, shared by the signed-in Value tab
 * and the anonymous public profile so both render the same count, order and
 * content. Nothing is collapsed: the prerendered HTML carries the whole plan
 * and there is no disclosure state to make accessible.
 */
export default function OnboardingPlan({ stages, id = ONBOARDING_PLAN_ANCHOR_ID }: OnboardingPlanProps) {
  if (stages.length === 0) return null;
  return (
    <section id={id} aria-label="New agent onboarding plan" className="scroll-mt-24">
      <SurfaceCard padding="lg" className="rounded-2xl">
        <SectionHead kicker="Onboarding" title="New agent onboarding plan" />
        <ol className="m-0 flex list-none flex-col gap-4 p-0">
          {stages.map((s) => {
            const label = milestoneLabel(s.milestone);
            return (
              <li key={s.id} className="rounded-xl border border-border bg-card p-5">
                <div className="flex flex-wrap items-center gap-3">
                  {label && <Tag tone="teal">{label}</Tag>}
                  <h3 className="m-0 text-[17px] font-semibold leading-snug text-ink-900">{s.title}</h3>
                </div>
                {s.description && <p className="mt-2 text-[14.5px] leading-[1.55] text-ink-700">{s.description}</p>}
                {(s.keyGoals.length > 0 || s.keyActivities.length > 0) && (
                  <div className="mt-4 grid gap-4 sm:grid-cols-2">
                    <StageList heading="What you will achieve" items={s.keyGoals} />
                    <StageList heading="How we support you" items={s.keyActivities} />
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      </SurfaceCard>
    </section>
  );
}
